import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChildren,
} from "@angular/core";
import { MatExpansionModule, MatExpansionPanel } from "@angular/material/expansion";
import { MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { MatDialog } from "@angular/material/dialog";
import { DetectionObject, DetectionResult, DetectionResultItem } from "../../../model/detection/detection.models";
import { DetectionService } from "../../../services/detection/detection.service";
import { SearchResultCardComponent } from "../../../search-display/search-results/result-card/search-result-card.component";
import { SearchResultItem } from "../../../model";
import { VIEWER_SERVICE } from "../../../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";
import { ExportDialogComponent } from "../../../../shared-components/export-dialog/export-dialog.component";
import { ExportDialogResult } from "../../../../shared-components/export-dialog/export-dialog.models";
import { CUSTOM_ELEMENTS_EXPORT_FORMATS } from "../../../model/detection/detection.tokens";
import { DetectionExportService } from "../../../services/detection/detection-export.service";
import { GeoService } from "../../../../viewer-components/services/internal-services/geo.service";
import { ColorSwatchPickerComponent } from "../../../../shared-components/color-swatch-picker/color-swatch-picker.component";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";

/**
 * Buffer applied to each side of the detection's bbox when building the
 * thumbnail's extract WKT. Expressed as a fraction of the longest bbox side
 * so cards of differently-sized objects (cars vs buildings) all frame the
 * detection consistently in the card.
 *
 * side_m = max(bboxW, bboxH) × (1 + 2·ratio)
 * → bbox occupies ~56% of the card's long axis (ratio = 0.4)
 */
const CARD_BUFFER_RATIO = 0.4;

/** Approx metres per degree of latitude (constant at any lat). */
const METERS_PER_LAT_DEG = 111320;

type SortMode = "conf-desc" | "conf-asc" | "id";

@Component({
  selector: "lib-detection-group",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatExpansionModule, MatIconButton, MatIcon, MatTooltip, SearchResultCardComponent, ColorSwatchPickerComponent],
  templateUrl: "./detection-group.component.html",
  styleUrl: "./detection-group.component.scss",
})
export class DetectionGroupComponent {
  protected readonly detection = inject(DetectionService);

  private readonly dialog = inject(MatDialog);

  private readonly exportService = inject(DetectionExportService);

  private readonly viewerService = inject(VIEWER_SERVICE);

  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  readonly object = input.required<DetectionObject>();

  readonly result = input.required<DetectionResult>();

  readonly visible = input(true);

  readonly expanded = input(false);

  protected readonly sortOptions: Array<{ value: SortMode; label: string }> = [
    { value: "conf-desc", label: "Conf ↓" },
    { value: "conf-asc", label: "Conf ↑" },
    { value: "id", label: "ID" },
  ];

  protected readonly bboxVisible = computed(() => this.detection.bboxVisibility()[this.object().name] !== false);

  protected readonly maskVisible = computed(() => this.detection.maskVisibility()[this.object().name] !== false);

  /** Debug flag (Alt+0) — shows a "merged from N" badge on fused-detection cards. */
  protected readonly debugMergedVisible = computed(() => this.detection.debugMergedVisible());

  protected readonly sortMode = signal<SortMode>("conf-desc");

  protected readonly sortedItems = computed(() => {
    const items = (this.result()?.detections ?? []).map((d, i) => ({ ...d, displayIndex: i + 1 }));
    const mode = this.sortMode();
    return [...items].sort((a, b) => {
      if (mode === "conf-asc") return a.confidence - b.confidence;
      if (mode === "id") return a.displayIndex - b.displayIndex;
      return b.confidence - a.confidence;
    });
  });

  private readonly expansionPanel = viewChildren(MatExpansionPanel);

  private readonly cardRefs = viewChildren(SearchResultCardComponent, { read: ElementRef });

  constructor() {
    effect(() => {
      const scroll = this.detection.scrollToDetection();
      if (!scroll || scroll.objectName !== this.object().name) return;

      // Expand the panel to make cards visible
      this.expansionPanel()[0]?.open();

      // After expansion animation, scroll to the target card and highlight it
      setTimeout(() => {
        const sortedIndex = this.sortedItems().findIndex((item) => item.displayIndex - 1 === scroll.detectionIndex);
        const cardEl = this.cardRefs()[sortedIndex]?.nativeElement as HTMLElement | undefined;
        if (cardEl) {
          cardEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
          const matCard = cardEl.children[0] as HTMLElement | undefined;
          if (matCard) {
            matCard.classList.add("highlighted");
            setTimeout(() => matCard.classList.remove("highlighted"), 1500);
          }
        }
        this.detection.setScrollToDetection(null);
      }, 250);
    });
  }

  protected readonly avgConfidence = computed(() => {
    const items = this.result()?.detections ?? [];
    if (!items.length) return 0;
    return items.reduce((s, d) => s + d.confidence, 0) / items.length;
  });

  /**
   * Adapts a DetectionResultItem to the shape SearchResultCardComponent expects.
   *
   * Geometry strategy:
   * - We compute a SQUARE WKT around the bbox client-side (not the server)
   *   so we know the rendered extent exactly. No `bufferPixel` is sent.
   * - `overlayRect` is then a deterministic function of the buffer ratio:
   *   the bbox occupies `bbox/side` of the card on each axis.
   */
  protected toSearchItem(item: DetectionResultItem & { displayIndex?: number }): SearchResultItem {
    const ctx = this.buildExtractContext(item.bboxWkt);
    return {
      key: `${item.imageId ?? "det"}-${item.displayIndex ?? ""}`,
      quadkey: "",
      similarity: item.confidence,
      geometry: ctx?.wkt ?? item.bboxWkt ?? "",
      drawingStyle: this.object().color,
      overlayRect: ctx?.overlayRect,
      maskOverlay:
        ctx?.overlayRect && item.maskRings ? this.buildMaskOverlay(item.maskRings, ctx.overlayRect) : undefined,
      imageLayer: item.sourceImageLayer,
      collection: item.sourceImageCollection ?? this.currentCollection(),
      centroid: item.centroid,
      score: item.confidence,
      scoreColor: this.confidenceColor(item.confidence),
    };
  }

  /**
   * Projects pre-computed doubled-grid rings onto the card's SVG coordinate space
   * (viewBox 0–100). Uses the same normalisation as the map layer: `hx/2/w`.
   */
  private buildMaskOverlay(
    maskRings: NonNullable<DetectionResultItem["maskRings"]>,
    overlayRect: { left: number; top: number; width: number; height: number },
  ): string {
    return maskRings.rings
      .map((ring) => {
        const points = ring
          .map(([hx, hy]) => {
            const x = overlayRect.left + (hx / 2 / maskRings.w) * overlayRect.width;
            const y = overlayRect.top + (hy / 2 / maskRings.h) * overlayRect.height;
            return `${x.toFixed(3)},${y.toFixed(3)}`;
          })
          .join(" L ");
        return `M ${points} Z`;
      })
      .join(" ");
  }

  /**
   * Builds (a) the square WKT to send to the extract API and (b) the overlay
   * rect (% of card) where the original bbox falls inside that area.
   *
   * The square is centred on the bbox and its side is `max(bboxDim) × (1 +
   * 2·CARD_BUFFER_RATIO)` metres. Because we send a square WKT, the server
   * renders that area 1:1 into the destination dimensions — no ambiguity
   * about how `bufferPixel`/`bufferMeter` are interpreted.
   */
  private buildExtractContext(
    bboxWkt: string | undefined,
  ): { wkt: string; overlayRect: { left: number; top: number; width: number; height: number } } | null {
    if (!bboxWkt) return null;

    const ext = GeoService.wktPolygonToBbox(bboxWkt, false) as [number, number, number, number] | undefined;
    if (!ext || ext.length < 4) return null;
    const [minLon, minLat, maxLon, maxLat] = ext;

    const centerLon = (minLon + maxLon) / 2;
    const centerLat = (minLat + maxLat) / 2;
    const cosLat = Math.cos((centerLat * Math.PI) / 180);
    const metersPerLonDeg = METERS_PER_LAT_DEG * cosLat;

    const bboxWMeters = Math.abs(maxLon - minLon) * metersPerLonDeg;
    const bboxHMeters = Math.abs(maxLat - minLat) * METERS_PER_LAT_DEG;
    if (bboxWMeters <= 0 || bboxHMeters <= 0) return null;

    const maxDimMeters = Math.max(bboxWMeters, bboxHMeters);
    const sideMeters = maxDimMeters * (1 + 2 * CARD_BUFFER_RATIO);
    const halfSideMeters = sideMeters / 2;

    const halfSideLon = halfSideMeters / metersPerLonDeg;
    const halfSideLat = halfSideMeters / METERS_PER_LAT_DEG;
    const wLon = centerLon - halfSideLon;
    const eLon = centerLon + halfSideLon;
    const sLat = centerLat - halfSideLat;
    const nLat = centerLat + halfSideLat;

    const wkt = `POLYGON((${wLon} ${sLat},${eLon} ${sLat},` + `${eLon} ${nLat},${wLon} ${nLat},${wLon} ${sLat}))`;

    const widthPct = (bboxWMeters / sideMeters) * 100;
    const heightPct = (bboxHMeters / sideMeters) * 100;
    return {
      wkt,
      overlayRect: {
        left: 50 - widthPct / 2,
        top: 50 - heightPct / 2,
        width: widthPct,
        height: heightPct,
      },
    };
  }

  private currentCollection(): string {
    const centerAOI = this.viewerService?.getProjectStatus?.()?.centerAOI;
    return centerAOI?.getName?.() ?? "";
  }

  /** Green ≥ 0.85, amber ≥ 0.70, red below — matches the prototype palette. */
  private confidenceColor(c: number): string {
    if (c >= 0.85) return "#2E9D6A";
    if (c >= 0.7) return "#E59B00";
    return "#C7361A";
  }

  protected openExport(event: MouseEvent): void {
    event.stopPropagation();
    const obj = this.object();
    this.dialog
      .open<ExportDialogComponent, unknown, ExportDialogResult>(ExportDialogComponent, {
        width: "var(--vx-dialog-w)",
        maxWidth: "95vw",
        autoFocus: false,
        data: {
          eyebrow: "Custom Elements · Detection results",
          title: "Export Detection Results",
          subtitle: "Choose an export format for this layer.",
          applyLabel: "Export Results",
          textSearchQuery: null,
          layers: [{ id: obj.name, name: obj.name, color: obj.color, count: this.result()?.detections.length ?? 0 }],
          preselectedLayerIds: [obj.name],
          formats: CUSTOM_ELEMENTS_EXPORT_FORMATS,
          defaultFormatId: "geojson",
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.exportService.exportResults(
            result.format,
            result.layers,
            this.detection.objects(),
            this.detection.results(),
          );
        }
      });
  }

  protected toggleBboxes(event: MouseEvent): void {
    event.stopPropagation();
    const next = !this.bboxVisible();
    this.detection.setBboxVisible(this.object().name, next);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.CUSTOM_ELEMENT_DETECTION_TOGGLE_BBOX,
      GoogleAnalyticsConstantsCategory.CUSTOM_ELEMENTS_DETECTION,
      next,
      this.object().name,
    );
  }

  protected toggleMasks(event: MouseEvent): void {
    event.stopPropagation();
    const next = !this.maskVisible();
    this.detection.setMaskVisible(this.object().name, next);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.CUSTOM_ELEMENT_DETECTION_TOGGLE_MASK,
      GoogleAnalyticsConstantsCategory.CUSTOM_ELEMENTS_DETECTION,
      next,
      this.object().name,
    );
  }

  protected changeColor(color: string): void {
    this.detection.updateObject(this.object().name, { color });
  }
}
